// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Skein — the deterministic game state. The committed action is place(i, x, y): move bead
// i to a normalized board position, which counts as one move iff it actually moved. Because
// every web is a pure function of the seed and place() is exact (no Math.random), the same
// web + the same placement sequence is byte-identical on every machine — which is what makes
// the same-seed race fair by construction and the score honest. tick() only advances the
// wall-clock (used solely by Cascade's throughput timer) and runs off the game screen's rAF.

import { countCrossings, type Edge, type Pt } from './geom';
import { buildGraph, type Graph } from './graph';
import type { Mode } from './modes';

/** Beads stay inside this inset of the board so a dragged bead never leaves the surface. */
export const MARGIN = 0.06;
const MOVE_EPS = 0.006; // a place within this of the current spot is a tap, not a move

export type GameEvent =
  | { t: 'move'; i: number }
  | { t: 'clear'; count: number } // crossings removed by the last move (spark juice)
  | { t: 'solve' } // the current web reached zero crossings
  | { t: 'next'; index: number } // Cascade advanced to a fresh web
  | { t: 'win' } // the run completed successfully (single web solved, or clock ran out)
  | { t: 'budget' } // Snarl: out of moves with crossings remaining — a loss
  | { t: 'timeout' }; // Cascade: the clock ended (a completion)

export interface GameState {
  mode: Mode;
  seed: number;
  index: number; // which web in a stream (0 for single-web modes)
  graph: Graph;
  nodes: Pt[]; // committed bead positions (mutated by place)
  edges: Edge[];
  crossings: number; // crossings in the committed layout (0 = this web solved)
  moves: number;
  solvedCount: number;
  elapsedMs: number;
  over: boolean;
  finished: boolean; // the run ended in success
  dnf: boolean; // ran out of moves (Snarl)
  reason: 'win' | 'timeout' | 'budget' | null;
  par: number; // reference moves for the current web
  events: GameEvent[];
}

function clamp01(v: number): number {
  return v < MARGIN ? MARGIN : v > 1 - MARGIN ? 1 - MARGIN : v;
}

export function createGame(mode: Mode, seed: number): GameState {
  const g = buildGraph(mode, seed, 0);
  return {
    mode,
    seed,
    index: 0,
    graph: g,
    nodes: g.start.map((p) => ({ ...p })),
    edges: g.edges,
    crossings: g.startCrossings,
    moves: 0,
    solvedCount: 0,
    elapsedMs: 0,
    over: false,
    finished: false,
    dnf: false,
    reason: null,
    par: g.par,
    events: [],
  };
}

export function drainEvents(s: GameState): GameEvent[] {
  if (s.events.length === 0) return [];
  const out = s.events;
  s.events = [];
  return out;
}

function loadWeb(s: GameState, index: number): void {
  const g = buildGraph(s.mode, s.seed, index);
  s.index = index;
  s.graph = g;
  s.nodes = g.start.map((p) => ({ ...p }));
  s.edges = g.edges;
  s.crossings = g.startCrossings;
  s.par = g.par;
}

function resolveSolve(s: GameState): void {
  s.events.push({ t: 'solve' });
  s.solvedCount++;
  if (s.mode.stream) {
    loadWeb(s, s.index + 1);
    s.events.push({ t: 'next', index: s.index });
  } else {
    s.over = true;
    s.finished = true;
    s.reason = 'win';
    s.events.push({ t: 'win' });
  }
}

/**
 * Move bead `i` to (x, y). Clamped to the board. A placement that does not actually move the
 * bead is a no-op tap (no move counted). Recomputes crossings; a web hitting zero solves it,
 * and in Snarl exhausting the move budget with crossings left ends the run.
 */
export function place(s: GameState, i: number, x: number, y: number): void {
  if (s.over) return;
  if (i < 0 || i >= s.nodes.length) return;
  const nx = clamp01(x);
  const ny = clamp01(y);
  const from = s.nodes[i];
  if (Math.hypot(nx - from.x, ny - from.y) <= MOVE_EPS) return;

  const before = s.crossings;
  s.nodes[i] = { x: nx, y: ny };
  s.moves++;
  const now = countCrossings(s.nodes, s.edges);
  s.crossings = now;
  s.events.push({ t: 'move', i });
  if (now < before) s.events.push({ t: 'clear', count: before - now });

  if (now === 0) {
    resolveSolve(s);
  } else if (s.mode.budget > 0 && s.moves >= s.mode.budget) {
    s.over = true;
    s.dnf = true;
    s.reason = 'budget';
    s.events.push({ t: 'budget' });
  }
}

/** Advance the wall-clock by `dtMs`. Only Cascade's clock can end a run this way. */
export function tick(s: GameState, dtMs: number): void {
  if (s.over) return;
  s.elapsedMs += Math.max(0, dtMs);
  if (s.mode.limitMs > 0 && s.elapsedMs >= s.mode.limitMs) {
    s.elapsedMs = s.mode.limitMs;
    s.over = true;
    s.finished = true;
    s.reason = 'timeout';
    s.events.push({ t: 'timeout' });
  }
}

/** Over par by how many moves on the current web (0 = a clean, par-or-better solve). */
export function overPar(s: GameState): number {
  return Math.max(0, s.moves - s.par);
}
